import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCreatePath, useNotify } from 'react-admin';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { SectionCard, fmtDate } from '../shared/Panel';
import { useHasPermission } from '../admin/PermissionGate';
import { toErrorMessage } from './preOrderApi';
import {
  MANUAL_LINK_ROLES,
  MIN_UNLINK_REASON,
  fetchEmailPreOrders,
  fetchPreOrderEmails,
  linkEmail,
  linkPreOrderToEmail,
  searchEmails,
  searchPreOrders,
  unlinkEmail,
  type EmailOption,
  type EmailPreOrder,
  type LinkRole,
  type PreOrderEmail,
  type PreOrderOption,
} from './preOrderDocumentsApi';
import { wallClockDateTime } from '../shared/clientTime';
import { preOrderStatusLabel } from '../shared/status';

/**
 * Maile ↔ prezlecenia w obie strony (03.11.2026, `preOrderDocuments`,
 * S-03.11-01). Jeden mail może dotyczyć kilku prezleceń, a jedno
 * prezlecenie kilku maili — na karcie prezlecenia lista maili, na karcie
 * maila lista prezleceń, z przejściem w obie strony. Powiązać i odpiąć
 * (zawsze z powodem) może przyjęcie zleceń; ślad w historii prezlecenia
 * i w dzienniku działań.
 */

const roleColor = (role: string): 'primary' | 'info' | 'default' | 'warning' =>
  role === 'SOURCE' ? 'primary' : role === 'SUPPLEMENT' ? 'info' : role === 'DUPLICATE_OF' ? 'warning' : 'default';

/** Pewność odczytu jako procent — tylko przy powiązaniu z odczytu maila. */
const confidenceLabel = (c: number | null) => (c == null ? null : `pewność ${Math.round(c * 100)}%`);

const RolePicker = ({ value, onChange }: { value: LinkRole; onChange: (r: LinkRole) => void }) => (
  <TextField
    select
    size="small"
    label="Rola maila"
    value={value}
    onChange={(e) => onChange(e.target.value as LinkRole)}
    data-testid="email-link-role"
    sx={{ minWidth: 200 }}
  >
    {MANUAL_LINK_ROLES.map((r) => (
      <MenuItem key={r.id} value={r.id}>
        {r.name}
      </MenuItem>
    ))}
  </TextField>
);

/** Okno odpięcia z powodem (min. 10 znaków) — wspólne dla obu stron. */
const UnlinkDialog = ({
  open,
  what,
  onClose,
  onConfirm,
}: {
  open: boolean;
  what: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);
  const ok = reason.trim().length >= MIN_UNLINK_REASON;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Odepnij {what}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Powiązanie zniknie z obu kart, a w historii prezlecenia zostanie wpis: kto, kiedy i dlaczego.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="Powód"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText={`Co najmniej ${MIN_UNLINK_REASON} znaków.`}
          slotProps={{ htmlInput: { 'data-testid': 'email-unlink-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!ok || busy}
          data-testid="email-unlink-confirm"
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(reason.trim());
              onClose();
            } catch (e) {
              setError(toErrorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Odepnij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Wyszukiwarka z podpowiedziami (maile albo prezlecenia). */
function useSearch<T>(search: (q: string) => Promise<T[]>, open: boolean) {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      search(q)
        .then((o) => alive && setOptions(o))
        .catch(() => alive && setOptions([]));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, open, search]);
  return { q, setQ, options };
}

/** Powiąż mail z prezleceniem — wybór maila ze skrzynki i roli. */
const LinkEmailDialog = ({
  open,
  onClose,
  onLinked,
  preOrderId,
}: {
  open: boolean;
  onClose: () => void;
  onLinked: (links: PreOrderEmail[]) => void;
  preOrderId: number;
}) => {
  const { setQ, options } = useSearch<EmailOption>(searchEmails, open);
  const [email, setEmail] = useState<EmailOption | null>(null);
  const [role, setRole] = useState<LinkRole>('CORRESPONDENCE');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail(null);
      setRole('CORRESPONDENCE');
      setNote('');
      setError(null);
    }
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Powiąż mail z prezleceniem</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete
            options={options}
            value={email}
            onChange={(_e, v) => setEmail(v)}
            onInputChange={(_e, v) => setQ(v)}
            filterOptions={(o) => o}
            getOptionLabel={(o) => `#${o.id} ${o.subject || '(bez tematu)'}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2">{o.subject || '(bez tematu)'}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    #{o.id} · {o.fromName ?? '—'} · {fmtDate(o.receivedAt)}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mail (temat, nadawca, treść)"
                size="small"
                slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, 'data-testid': 'email-link-search' } }}
              />
            )}
          />
          <RolePicker value={role} onChange={setRole} />
          <TextField size="small" label="Uwaga (opcjonalnie)" value={note} onChange={(e) => setNote(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={!email || busy}
          data-testid="email-link-confirm"
          onClick={async () => {
            if (!email) return;
            setBusy(true);
            try {
              onLinked(await linkEmail(preOrderId, { emailId: email.id, role, note: note.trim() || undefined }));
              onClose();
            } catch (e) {
              setError(toErrorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Powiąż
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Karta prezlecenia: maile tego prezlecenia. */
export const PreOrderEmailsCard = ({ preOrderId, onChanged }: { preOrderId: number; onChanged?: () => void }) => {
  const createPath = useCreatePath();
  const notify = useNotify();
  const canIntake = useHasPermission('ORDER_INTAKE');
  const [links, setLinks] = useState<PreOrderEmail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<PreOrderEmail | null>(null);
  const load = useCallback(() => {
    fetchPreOrderEmails(preOrderId)
      .then((l) => {
        setLinks(l);
        setError(null);
      })
      .catch((e) => setError(toErrorMessage(e)));
  }, [preOrderId]);
  useEffect(() => load(), [load]);
  const changed = (l: PreOrderEmail[], msg: string) => {
    setLinks(l);
    notify(msg, { type: 'info' });
    onChanged?.();
  };
  return (
    <SectionCard title={`Maile prezlecenia${links ? ` (${links.length})` : ''}`}>
      <Box data-testid="pre-order-emails">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {links && links.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Z prezleceniem nie jest powiązany żaden mail.
          </Typography>
        ) : null}
        <Stack spacing={1}>
          {(links ?? []).map((l) => (
            <Box
              key={l.emailId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}
              data-testid="pre-order-email-row"
              data-email-id={l.emailId}
            >
              <Chip size="small" color={roleColor(l.role)} label={l.roleLabel} />
              <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {l.subject || `Mail #${l.emailId}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {[l.fromName, fmtDate(l.receivedAt ?? l.sentAt), l.originLabel, confidenceLabel(l.confidence), l.linkedBy ? `powiązał: ${l.linkedBy}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              {l.attachmentCount > 0 ? (
                <Chip size="small" variant="outlined" icon={<AttachFileIcon />} label={l.attachmentCount} title="Załączniki maila" />
              ) : null}
              {canIntake ? (
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  to={createPath({ resource: 'emails', type: 'show', id: l.emailId })}
                  data-testid="pre-order-email-open"
                >
                  Otwórz mail
                </Button>
              ) : null}
              {canIntake ? (
                <Button size="small" color="warning" startIcon={<LinkOffIcon />} onClick={() => setUnlinking(l)} data-testid="pre-order-email-unlink">
                  Odepnij
                </Button>
              ) : null}
            </Box>
          ))}
        </Stack>
        {canIntake ? (
          <Button size="small" startIcon={<LinkIcon />} sx={{ mt: 1 }} onClick={() => setLinking(true)} data-testid="pre-order-email-link">
            Powiąż mail
          </Button>
        ) : null}
      </Box>
      <LinkEmailDialog
        open={linking}
        preOrderId={preOrderId}
        onClose={() => setLinking(false)}
        onLinked={(l) => changed(l, 'Mail powiązany z prezleceniem.')}
      />
      <UnlinkDialog
        open={unlinking !== null}
        what={unlinking ? `mail „${unlinking.subject || `#${unlinking.emailId}`}”` : 'mail'}
        onClose={() => setUnlinking(null)}
        onConfirm={async (reason) => {
          if (!unlinking) return;
          changed(await unlinkEmail(preOrderId, unlinking.emailId, reason), 'Mail odpięty od prezlecenia.');
        }}
      />
    </SectionCard>
  );
};

/** Powiąż prezlecenie z mailem — od strony maila. */
const LinkPreOrderDialog = ({
  open,
  onClose,
  onLinked,
  emailId,
}: {
  open: boolean;
  onClose: () => void;
  onLinked: (links: EmailPreOrder[]) => void;
  emailId: number;
}) => {
  const { setQ, options } = useSearch<PreOrderOption>(searchPreOrders, open);
  const [preOrder, setPreOrder] = useState<PreOrderOption | null>(null);
  const [role, setRole] = useState<LinkRole>('SOURCE');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setPreOrder(null);
      setRole('SOURCE');
      setError(null);
    }
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Powiąż mail z prezleceniem</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete
            options={options}
            value={preOrder}
            onChange={(_e, v) => setPreOrder(v)}
            onInputChange={(_e, v) => setQ(v)}
            filterOptions={(o) => o}
            getOptionLabel={(o) => `#${o.id} ${o.clientName ?? ''} ${o.relation ?? ''}`.trim()}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Prezlecenie (klient, miasto, numer zamówienia)"
                size="small"
                slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, 'data-testid': 'pre-order-link-search' } }}
              />
            )}
          />
          <RolePicker value={role} onChange={setRole} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={!preOrder || busy}
          data-testid="pre-order-link-confirm"
          onClick={async () => {
            if (!preOrder) return;
            setBusy(true);
            try {
              onLinked(await linkPreOrderToEmail(emailId, { preOrderId: preOrder.id, role }));
              onClose();
            } catch (e) {
              setError(toErrorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Powiąż
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Karta maila: prezlecenia z tego maila (wiele do wielu). */
export const EmailPreOrdersCard = ({ emailId }: { emailId: number }) => {
  const createPath = useCreatePath();
  const notify = useNotify();
  const [links, setLinks] = useState<EmailPreOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<EmailPreOrder | null>(null);
  const load = useCallback(() => {
    fetchEmailPreOrders(emailId)
      .then((l) => {
        setLinks(l);
        setError(null);
      })
      .catch((e) => setError(toErrorMessage(e)));
  }, [emailId]);
  useEffect(() => load(), [load]);
  return (
    <SectionCard title={`Prezlecenia z tego maila${links ? ` (${links.length})` : ''}`}>
      <Box data-testid="email-pre-orders">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {links && links.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mail nie jest powiązany z żadnym prezleceniem.
          </Typography>
        ) : null}
        <Stack spacing={1}>
          {(links ?? []).map((l) => (
            <Box
              key={l.preOrderId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}
              data-testid="email-pre-order-row"
              data-pre-order-id={l.preOrderId}
            >
              <Chip size="small" color={roleColor(l.role)} label={l.roleLabel} />
              <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
                <Typography variant="body2">
                  #{l.preOrderId} {l.clientName ?? ''}
                  {l.loadingCity || l.unloadingCity ? ` · ${l.loadingCity ?? '?'} → ${l.unloadingCity ?? '?'}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {[
                    preOrderStatusLabel(l.status),
                    l.orderNumber ? `zlecenie ${l.orderNumber}` : null,
                    l.customerOrderNumber ? `nr klienta ${l.customerOrderNumber}` : null,
                    l.loadingDate ? `załadunek ${wallClockDateTime(l.loadingDate)}` : null,
                    l.originLabel,
                    confidenceLabel(l.confidence),
                    l.archived ? 'zarchiwizowane' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                component={Link}
                to={createPath({ resource: 'pre-orders', type: 'show', id: l.preOrderId })}
                data-testid="email-pre-order-open"
              >
                Otwórz prezlecenie
              </Button>
              <Button size="small" color="warning" startIcon={<LinkOffIcon />} onClick={() => setUnlinking(l)} data-testid="email-pre-order-unlink">
                Odepnij
              </Button>
            </Box>
          ))}
        </Stack>
        <Button size="small" startIcon={<LinkIcon />} sx={{ mt: 1 }} onClick={() => setLinking(true)} data-testid="email-pre-order-link">
          Powiąż z prezleceniem
        </Button>
      </Box>
      <LinkPreOrderDialog
        open={linking}
        emailId={emailId}
        onClose={() => setLinking(false)}
        onLinked={(l) => {
          setLinks(l);
          notify('Mail powiązany z prezleceniem.', { type: 'info' });
        }}
      />
      <UnlinkDialog
        open={unlinking !== null}
        what={unlinking ? `prezlecenie #${unlinking.preOrderId}` : 'prezlecenie'}
        onClose={() => setUnlinking(null)}
        onConfirm={async (reason) => {
          if (!unlinking) return;
          await unlinkEmail(unlinking.preOrderId, emailId, reason);
          notify('Mail odpięty od prezlecenia.', { type: 'info' });
          load();
        }}
      />
    </SectionCard>
  );
};
